import { Link, useParams } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { Button, Card, CardContent } from '@/components/ui'
import { formatPriceCents, getBrandBySlug, getBrandModel, getBrandModelYears } from '@/data/brands'
import { useSeo } from '@/lib/seo'

export function BrandModelPage() {
  const { brand: brandSlug, model: modelSlug } = useParams<{ brand: string; model: string }>()
  const brand = brandSlug ? getBrandBySlug(brandSlug) : undefined
  const model = brandSlug && modelSlug ? getBrandModel(brandSlug, modelSlug) : undefined
  const years = brandSlug && modelSlug ? getBrandModelYears(brandSlug, modelSlug) : []

  if (brand && model) {
    useSeo({
      title: model.seo?.title ?? `${brand.name} ${model.name} | Bespokian Auto`,
      description: model.seo?.description ?? model.description,
      image: `/brand-images/${brand.slug}.jpg`,
    })
  }

  if (!brand || !model) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="container-page py-16">
          <Card className="max-w-xl mx-auto text-center">
            <CardContent className="py-10">
              <h1 className="text-2xl font-bold text-gray-900">Page not found</h1>
              <p className="mt-2 text-gray-600">
                We couldn’t find that brand/model combination.
              </p>
              <Link to="/brands" className="inline-block mt-6">
                <Button>Browse Brands</Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="container-page py-8">
        <div className="mb-8">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/brands" className="hover:underline">
              Brands
            </Link>
            <span>/</span>
            <Link to={`/brands/${brand.slug}`} className="hover:underline">
              {brand.name}
            </Link>
            <span>/</span>
            <span className="font-medium text-gray-900">{model.name}</span>
          </div>

          <h1 className="mt-2 text-3xl font-bold text-gray-900">
            {brand.name} {model.name}
          </h1>
          <p className="mt-2 text-gray-600 max-w-3xl">
            {model.description}
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <Card>
              <CardContent>
                <h2 className="text-lg font-semibold text-gray-900">Choose a Year</h2>
                <p className="mt-1 text-sm text-gray-600">
                  These links point to dedicated year pages under this model.
                </p>

                <div className="mt-6 grid grid-cols-2 sm:grid-cols-5 gap-3">
                  {years.map((y) => (
                    <Link
                      key={y}
                      to={`/brands/${brand.slug}/${model.slug}/${y}`}
                      className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-center text-sm font-medium text-gray-900 transition-colors hover:border-primary-300 hover:bg-primary-50"
                    >
                      {y}
                    </Link>
                  ))}
                </div>

                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  <div className="rounded-lg border border-gray-200 p-4">
                    <p className="text-sm text-gray-600">Starting around</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatPriceCents(model.startingPrice)}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      Based on client-supplied model dataset
                    </p>
                  </div>
                  <div className="rounded-lg border border-gray-200 p-4">
                    <p className="text-sm text-gray-600">Inventory count (mock)</p>
                    <p className="text-2xl font-bold text-gray-900">{model.inventoryCount}</p>
                    <p className="mt-1 text-xs text-gray-500">
                      Used for SEO page templates
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="mt-6">
              <CardContent>
                <h2 className="text-lg font-semibold text-gray-900">Highlights</h2>
                <ul className="mt-3 space-y-2 text-sm text-gray-600">
                  {model.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-2">
                      <span className="mt-1 h-2 w-2 rounded-full bg-primary-600 flex-shrink-0" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="mt-6">
              <CardContent>
                <h2 className="text-lg font-semibold text-gray-900">Key specs</h2>
                <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  {model.specs.map((s) => (
                    <div key={s.label} className="rounded-lg border border-gray-200 p-4">
                      <dt className="text-gray-500">{s.label}</dt>
                      <dd className="font-medium text-gray-900 mt-1">{s.value}</dd>
                    </div>
                  ))}
                </dl>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="bg-primary-50 border-primary-200">
              <CardContent>
                <h3 className="font-semibold text-gray-900">Browse inventory</h3>
                <p className="mt-2 text-sm text-gray-600">
                  Jump to the inventory filtered by make for this model.
                </p>
                <Link to={`/vehicles?make=${encodeURIComponent(brand.slug)}`} className="inline-block mt-4">
                  <Button>
                    View {brand.name} Vehicles
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <h3 className="font-semibold text-gray-900">About {brand.name}</h3>
                <p className="mt-2 text-sm text-gray-600">{brand.description}</p>
                <Link to={`/brands/${brand.slug}`} className="inline-block mt-4">
                  <Button variant="outline">Back to {brand.name}</Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}

